import store from '@/store';

const ONBOARDING_STEPS = [
  '/console/onboarding/terms',
  '/console/onboarding/customer-type',
  '/console/onboarding/subscription',
  '/console/onboarding/billing',
  '/console/onboarding/checkout',
];

const ENTERPRISE_ALLOWED_PATHS = new Set([
  '/console/enterprise/subscription',
  '/logout',
  '/eula',
]);

function getFirstIncompleteStep(userData) {
  // Org members have no individual subscription — skip onboarding entirely
  if (userData.is_org_member)                         return null;

  const requiredTermsVersion = Number(process.env.VUE_APP_TERMS_VERSION);

  const hasAcceptedTerms =
    userData.terms_version !== null &&
    userData.terms_version >= requiredTermsVersion;

  if (!hasAcceptedTerms)                              return '/console/onboarding/terms';

  // Trial users bypass everything after terms
  if (userData.is_trial)                              return null;

  if (!userData.customer_type)                        return '/console/onboarding/customer-type';

  // Capture-only users have no SDK tier to select
  const isCaptureOnly = userData.customer_type === 'capture';
  const hasSelectedTier =
    isCaptureOnly || userData.tier != null || store.state.selectedTier;

  const hasBillingInfo = userData.has_billing_info;

  if (!hasSelectedTier)                                              return '/console/onboarding/subscription';
  if (!hasBillingInfo)                                               return '/console/onboarding/billing';
  if (!isCaptureOnly && store.state.selectedTier && !userData.tier) return '/console/onboarding/checkout';
  if (isCaptureOnly && userData.account_status === 'Pending')       return '/console/onboarding/checkout';
  return null; // fully onboarded
}

export function onboardingGuard(to, _from, next) {
  // Public routes and admin users were already let through.
  if (to.meta.public || !to.meta._userData) return next();

  // Checkout return always bypasses onboarding
  if (to.path === '/console/checkout/return') return next();

  const userData = to.meta._userData;
  const isOnboardingRoute = ONBOARDING_STEPS.includes(to.path);
  const firstStep = getFirstIncompleteStep(userData);

  // ── Fully onboarded ──────────────────────────────────────────────────────
  if (!firstStep) {
    if (isOnboardingRoute) {
      const target = userData.customer_type === 'capture' ? '/console/account' : '/console/getstarted';
      return next(target);
    }

    // Enterprise + quote but no contract → force quote conversion
    if (
      userData.tier === 'Enterprise' &&
      userData.has_quote &&
      !userData.is_trial &&
      !ENTERPRISE_ALLOWED_PATHS.has(to.path)
    ) {
      return next('/console/enterprise/subscription');
    }

    // Pending non-Enterprise → account only
    if (
      userData.account_status === 'Pending' &&
      userData.tier !== 'Enterprise' &&
      to.path !== '/console/account'
    ) {
      return next('/console/account');
    }

    return next();
  }

  // ── Incomplete onboarding ────────────────────────────────────────────────

  // Trying to visit terms but already accepted → skip to next step
  if (to.path === '/console/onboarding/terms' && firstStep !== '/console/onboarding/terms') {
    return next(firstStep);
  }

  // Block protected routes until onboarding is done
  if (!to.meta.public && !isOnboardingRoute) {
    return next(firstStep);
  }

  // Capture-only users jump from customer-type to billing (skip subscription step)
  if (
    to.path === '/console/onboarding/subscription' &&
    userData.customer_type === 'capture'
  ) {
    return next('/console/onboarding/billing');
  }

  // Prevent skipping ahead in onboarding
  const targetIndex     = ONBOARDING_STEPS.indexOf(to.path);
  const incompleteIndex = ONBOARDING_STEPS.indexOf(firstStep);
  if (isOnboardingRoute && targetIndex > incompleteIndex) {
    return next(firstStep);
  }

  return next();
}
